import {
  ErrorMessage,
  Spinner,
  Button,
  SuccessPopup,
} from "@/common/components";
import { useState, useContext, useEffect, useNavigate } from "@/common/hooks";
import {
  useGetAccessProfileForProfileQuery,
  useGetAllAccessProfilesForProfileQuery,
  useUpdateAccessProfileForProfileMutation,
} from "@/service/ProfileService";
import {
  useGetInsuranceCompaniesForEnrollmentQuery,
  useGetProductManufacturersQuery,
} from "@/service/APIService";
import { PrivateContext } from "@/routes/PrivateRoute";
import AccessOptions from "@/components/Common/AccessOptions";
import Routes from "@/routes/RouteComponent";

const UpdateAccessLevel = () => {
  const { labels, cloneDeep, cancel, setNavHeader, setPageHeader, skipToken } =
    useContext(PrivateContext);
  const navigate = useNavigate();
  const [selectedEntity, setSelectedEntity] = useState("");
  const [insuranceId, setInsuranceId] = useState(skipToken);
  const [companyType, setCompanyType] = useState("");
  const [showPopup, setShowPopup] = useState();

  const defaultIdObj = {
    accessProfileId: "",
    productMfgId: "",
  };
  const [selectedIds, setSelectedIds] = useState({
    I: [cloneDeep(defaultIdObj)],
    P: [cloneDeep(defaultIdObj)],
    R: [cloneDeep(defaultIdObj)],
  });
  const [productManufacturers, setProductManufacturers] = useState([]);
  const [approveAccessProfile, setApproveAccessProfile] = useState([]);

  const {
    data: accessProfiles,
    isFetching,
    error,
  } = useGetAllAccessProfilesForProfileQuery();

  const {
    data: currentAccessProfile,
    isFetching: isFetching1,
    error: error1,
  } = useGetAccessProfileForProfileQuery();

  const {
    data: insuranceCompanies,
    isFetching: isFetching2,
    error: error2,
  } = useGetInsuranceCompaniesForEnrollmentQuery();

  const [updateAccessLevelApi, { isLoading: isFetching3, error: error3 }] =
    useUpdateAccessProfileForProfileMutation();

  const { data: insuranceManufacturers, isFetching: isFetching4 } =
    useGetProductManufacturersQuery(insuranceId);

  useEffect(() => {
    if (currentAccessProfile) {
      if (currentAccessProfile?.contact?.insuranceCompanyId) {
        setSelectedEntity("I");
        setInsuranceId({
          insuranceCompanyId: currentAccessProfile?.contact?.insuranceCompanyId,
        });
      } else if (currentAccessProfile?.contact?.productSponsorId) {
        setSelectedEntity("P");
      } else {
        setSelectedEntity("R");
      }
    }
  }, [currentAccessProfile]);

  useEffect(() => {
    if (currentAccessProfile && selectedEntity != "") {
      const productSponsors = currentAccessProfile.dataAccessRuleList.map(
        (e) => e.productSponsor
      );
      setProductManufacturers(productSponsors);

      const approveAccessProfile = currentAccessProfile.dataAccessRuleList.map(
        (e) => {
          return {
            ...e.approveAccessProfile,
            productSponsorId: e?.productSponsor?.id,
          };
        }
      );
      setApproveAccessProfile(approveAccessProfile);

      const requestAccessProfile = currentAccessProfile.dataAccessRuleList.map(
        (e) => e.requestAccessProfile
      );
      requestAccessProfile.forEach((e, index) => {
        selectedIds[selectedEntity][index] = {
          productMfgId: productSponsors[index].id,
          accessProfileId: e?.id,
        };
      });
      setSelectedIds({ ...selectedIds });
    }
  }, [selectedEntity, insuranceManufacturers]);

  useEffect(() => {
    if (insuranceManufacturers && currentAccessProfile) {
      setProductManufacturers(insuranceManufacturers?.productSponsors);
      insuranceManufacturers?.productSponsors.forEach((e, index) => {
        selectedIds[selectedEntity][index] = {
          productMfgId: e.id,
          accessProfileId: currentAccessProfile?.dataAccessRuleList?.find(
            (e1) => e1.productSponsor.id === e.id
          )?.requestAccessProfile?.id,
        };
      });
      setSelectedIds({ ...selectedIds });
    }
  }, [insuranceManufacturers]);

  useEffect(() => {
    setNavHeader(labels?.navbar?.profile);
    setPageHeader(labels?.updateAccessLevel?.title);
  }, []);

  // ---- Updated function starts here ----
  const doSendRequest = () => {
    const PROMPT_ID = "-1"; // "-- select --" sentinel (do not send)
    // const NO_ACCESS_ID = "-2"; // "No access" sentinel (we allow sending this)

    // deep clone to avoid mutating original state objects
    let dataAccessRuleList = cloneDeep(currentAccessProfile.dataAccessRuleList);

    for (let i = 0; i < dataAccessRuleList.length; i++) {
      const dar = dataAccessRuleList[i];

      const findAccessProfile = selectedIds[selectedEntity]?.find(
        (e) => e.productMfgId === dar?.productSponsor?.id
      );

      // If a selection exists and it's not the prompt id, set it.
      // This permits "-2" (No access) to be set and submitted.
      if (
        findAccessProfile &&
        findAccessProfile.accessProfileId != null &&
        findAccessProfile.accessProfileId !== "" &&
        findAccessProfile.accessProfileId !== PROMPT_ID
      ) {
        dar.requestAccessProfile = {
          ...(dar.requestAccessProfile || {}),
          id: findAccessProfile.accessProfileId,
        };
      } else {
        // If the user hasn't selected anything or chose the prompt, ensure we don't send it
        dar.requestAccessProfile = null;
      }

      // Ensure approveAccessProfile exists for server expectations
      if (dar.approveAccessProfile == null) {
        dar.approveAccessProfile = { id: null };
      }

      if (dar?.requestAccessProfile?.id == "0") {
        dar.requestAccessProfile = { id: "-2" };
      }
    }

    // Filter out entries with no meaningful requestAccessProfile, explicitly excluding prompt id and empty id
    const filteredList = dataAccessRuleList.filter(
      (e) =>
        e.requestAccessProfile != null &&
        e.requestAccessProfile.id != null &&
        e.requestAccessProfile.id !== "" &&
        e.requestAccessProfile.id !== PROMPT_ID
    );

    const payload = {
      dataAccessRuleList: filteredList,
    };

    updateAccessLevelApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          if (filteredList[0].requestAccessProfile?.id == "-2") {
            navigate(Routes.logout.path);
          } else {
            setShowPopup(true);
          }
        }
      })
      .catch(() => {
        // keep existing behavior — error handled by ErrorMessage via RTK query error
      });
  };
  // ---- Updated function ends here ----

  return (
    <section className="container-large py-5">
      {(isFetching ||
        isFetching1 ||
        isFetching2 ||
        isFetching3 ||
        isFetching4) && <Spinner />}
      <ErrorMessage
        error={error || error1 || error2 || error3}
        wrapperClassName="mb-3"
      />

      <AccessOptions
        showHeading={false}
        enrollmentFlow={false}
        selectedEntity={selectedEntity}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        accessProfiles={accessProfiles}
        insuranceCompanies={insuranceCompanies}
        productManufacturers={productManufacturers}
        allProductManufacturers={productManufacturers}
        approveAccessProfile={approveAccessProfile}
        companyType={companyType}
        setCompanyType={setCompanyType}
        labels={labels?.updateAccessLevel}
      />
      <p className="mt-4 mb-5">{labels?.updateAccessLevel?.submit}</p>
      <div className="d-flex justify-content-md-end justify-content-end mt-5">
        <Button
          id="cancel"
          text={labels?.updateAccessLevel?.cancel}
          click={cancel}
          type="secondary"
          wrapperClassName="col-md-2 me-1 col-6"
        />
        <Button
          id="send"
          text={labels?.updateAccessLevel?.sendRequest}
          click={doSendRequest}
          wrapperClassName="col-lg-2 col-md-3 me-1 col-6"
        />
      </div>
      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.updateAccessLevel?.continue}
        click={cancel}
        body={labels?.updateAccessLevel?.success}
      />
    </section>
  );
};

export default UpdateAccessLevel;
